/**
 * Returns the appropriate icon and color for a power source based on battery bars and type.
 *
 * @param {number} batteryBars - Battery level (0-5)
 * @param {number|null} batteryType - Power source type (0-4), null defaults to battery
 * @returns {{ icon: string, color: string }}
 */
export function powerSourceIcon(batteryBars, batteryType = null) {
    // External Power (type 3)
    if (batteryType === 3) {
        return { icon: 'mdi-power-plug', color: 'green' };
    }
    // Super Capacitor (type 4)
    if (batteryType === 4) {
        return { icon: 'mdi-flash', color: 'amber' };
    }
    // Battery types (0, 1, 2, or null/undefined)
    return batteryIcon(batteryBars);
}

/**
 * Type of the SKU's SELECTED battery channel, for the power-source icon.
 * battery1_status / battery2_status choose which channel a SKU reports on
 * (channel 1 wins); the other channel's type is not the user-facing power
 * source. `battery1_type ?? battery2_type` picked channel 1's SuperCap (4) on
 * every OBD/Fleet SKU whose selected channel 2 is external power (3), drawing
 * the amber flash instead of the green plug. Flags can live on a different
 * object than the types (AssetDetailsResource keeps them under
 * device.product_sku), hence `flags`. Rows without flags keep the old
 * first-non-null fallback.
 *
 * @param {object|null} row - carries battery1_type / battery2_type
 * @param {object|null} flags - carries battery1_status / battery2_status
 * @returns {number|null}
 */
export function primaryBatteryType(row, flags = row) {
    if (!row) return null;
    if (flags?.battery1_status) return row.battery1_type ?? null;
    if (flags?.battery2_status) return row.battery2_type ?? null;
    return row.battery1_type ?? row.battery2_type ?? null;
}

/**
 * Standard battery icon based on bars level (existing logic extracted).
 */
export function batteryIcon(batteryBars) {
    const bars = batteryBars ?? 0;
    if (bars <= 0) return { icon: 'mdi-battery-off-outline', color: 'red' };
    if (bars === 1) return { icon: 'mdi-battery-low', color: 'red' };
    if (bars === 2) return { icon: 'mdi-battery-medium', color: 'amber' };
    return { icon: 'mdi-battery-high', color: 'green' };
}
